/**
 * Pixel UI widgets (doc 10 §Pixel UI, doc 17 §8).
 *
 * Every widget here is drawn, not styled: panels are 9-slice pixel frames,
 * interaction states are exact palette steps, and text sits on the 4 px
 * baseline grid. Nothing in the shipping game is DOM.
 */

import { font, type BitmapFont } from '../render/font/font.ts';
import { PixelSurface, type Color } from '../render/surface.ts';
import { g, UI } from './theme.ts';

export type WidgetState = 'default' | 'hover' | 'active' | 'selected' | 'disabled';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function contains(r: Rect, x: number, y: number): boolean {
  return x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h;
}

/** 50% checkerboard, the one dither pattern (doc 10). */
function dither(s: PixelSurface, r: Rect, color: Color, parity = 0): void {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      if (((x + y) & 1) === parity) s.px(x, y, color);
    }
  }
}

/** 9-slice panel: dithered drop shadow, 1 px dark rim, 1 px bright rim, fill. */
export function drawPanel(s: PixelSurface, r: Rect, opts: { raised?: boolean } = {}): void {
  dither(s, { x: r.x + 2, y: r.y + 2, w: r.w, h: r.h }, UI.shadow);
  s.fillRect(r.x, r.y, r.w, r.h, opts.raised ? UI.panelFillHi : UI.panelFill);
  s.strokeRect(r.x, r.y, r.w, r.h, UI.panelRimDark);
  s.strokeRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2, UI.panelRimLight);
}

/** Panel with a Header-face title bar. Returns the content rect below it. */
export function drawTitledPanel(s: PixelSurface, r: Rect, title: string): Rect {
  drawPanel(s, r);
  const f = font('header');
  f.draw(s, title, r.x + g(2), r.y + g(2), UI.ink, { caps: true, letterSpacing: 1 });
  const y = r.y + g(2) + f.capHeight + g(2);
  s.hline(r.x + 2, r.x + r.w - 3, y, UI.panelRimLight);
  return { x: r.x + g(2), y: y + g(2), w: r.w - g(4), h: r.h - (y + g(2) - r.y) - g(2) };
}

/**
 * Interaction states, exact (doc 10):
 *   hover    rim one step brighter, content up 1 px
 *   active   fill one step darker, content down 1 px
 *   selected persistent bright rim + corner notches
 *   disabled 50% checkerboard + desaturated row
 */
export function drawButton(
  s: PixelSurface,
  r: Rect,
  label: string,
  state: WidgetState,
  opts: { hotkey?: string; face?: 'micro' | 'body' } = {},
): void {
  const nudge = state === 'hover' ? -1 : state === 'active' ? 1 : 0;
  const fill = state === 'active' ? UI.panelFill : state === 'hover' ? UI.panelFillHi : UI.panelFill;
  s.fillRect(r.x, r.y, r.w, r.h, fill);
  s.strokeRect(r.x, r.y, r.w, r.h, UI.panelRimDark);
  s.strokeRect(
    r.x + 1,
    r.y + 1,
    r.w - 2,
    r.h - 2,
    state === 'hover' || state === 'selected' ? UI.accent : UI.panelRimLight,
  );

  const f = font(opts.face ?? 'body');
  const tw = f.measure(label, { caps: true });
  f.draw(
    s,
    label,
    r.x + ((r.w - tw) >> 1),
    r.y + ((r.h - f.capHeight) >> 1) + nudge,
    state === 'disabled' ? UI.inkDim : UI.ink,
    { caps: true },
  );

  if (opts.hotkey !== undefined) {
    const m = font('micro');
    const kw = m.measure(opts.hotkey, { caps: true });
    s.fillRect(r.x + r.w - kw - 5, r.y + 2, kw + 3, m.capHeight + 3, UI.panelRimDark);
    m.draw(s, opts.hotkey, r.x + r.w - kw - 3, r.y + 4, UI.inkDim, { caps: true });
  }

  if (state === 'selected') {
    for (const [dx, dy] of [
      [0, 0],
      [r.w - 1, 0],
      [0, r.h - 1],
      [r.w - 1, r.h - 1],
    ] as Array<[number, number]>) {
      s.px(r.x + dx, r.y + dy, UI.ink);
    }
  }
  if (state === 'disabled') dither(s, r, UI.disabled);
}

/**
 * Tooltip: name, hotkey chip, one-line effect, cost. Doc 17 §8 — an icon button
 * without one of these is a bug. Never covers the element it describes, so the
 * caller passes the anchor rect and we flip around it at the screen edge.
 */
export interface TooltipContent {
  name: string;
  hotkey?: string;
  effect: string;
  cost?: string;
  /** The thing itself, drawn at native size beside the text (doc 10). */
  thumb?: PixelSurface;
}

/** Gap between a tooltip's thumbnail and its text column. */
const THUMB_GAP = 6;

function thumbWidth(t: TooltipContent): number {
  return t.thumb ? t.thumb.width + THUMB_GAP : 0;
}

export function measureTooltip(t: TooltipContent): { w: number; h: number } {
  const body = font('body');
  const micro = font('micro');
  const textW = Math.max(
    body.measure(t.name, { caps: true }) + (t.hotkey ? micro.measure(t.hotkey) + g(3) : 0),
    body.measure(t.effect),
    t.cost ? body.measure(t.cost) : 0,
  );
  const w = textW + thumbWidth(t) + g(4);
  const textH = g(3) + body.capHeight + g(2) + body.capHeight + (t.cost ? g(1) + body.capHeight : 0) + g(2);
  const h = Math.max(textH, (t.thumb?.height ?? 0) + g(3));
  return { w, h };
}

export function drawTooltip(
  s: PixelSurface,
  t: TooltipContent,
  anchor: Rect,
  bounds: { w: number; h: number },
): void {
  const size = measureTooltip(t);
  // ABOVE the anchor, centred on it — a build button sits at the bottom edge of
  // the screen, and a tooltip beside it covers its neighbours (doc 17 §6).
  let x = anchor.x + ((anchor.w - size.w) >> 1);
  let y = anchor.y - size.h - g(2);
  x = Math.max(g(2), Math.min(x, bounds.w - size.w - g(2)));
  if (y < g(2)) y = Math.min(anchor.y + anchor.h + g(2), bounds.h - size.h - g(2));

  drawPanel(s, { x, y, w: size.w, h: size.h }, { raised: true });
  const body = font('body');
  const micro = font('micro');
  const textX = x + g(2) + thumbWidth(t);
  if (t.thumb) {
    s.blit(t.thumb, x + g(2), y + ((size.h - t.thumb.height) >> 1));
    s.vline(textX - (THUMB_GAP >> 1), y + g(1), y + size.h - g(1) - 1, UI.panelRimDark);
  }
  let pen = body.draw(s, t.name, textX, y + g(2), UI.ink, { caps: true });
  if (t.hotkey !== undefined) {
    const kw = micro.measure(t.hotkey, { caps: true });
    s.fillRect(pen + g(1), y + g(2), kw + 3, micro.capHeight + 3, UI.panelRimDark);
    micro.draw(s, t.hotkey, pen + g(1) + 2, y + g(2) + 2, UI.inkDim, { caps: true });
  }
  body.draw(s, t.effect, textX, y + g(2) + body.capHeight + g(2), UI.inkDim);
  if (t.cost !== undefined) {
    body.draw(s, t.cost, textX, y + g(2) + body.capHeight * 2 + g(3), UI.warning);
  }
}

/**
 * A stat row: label left, value right, tabular digits, units inside the value.
 * This is the single reason a column of numbers scans vertically (doc 17 §8).
 */
export function drawStatRow(
  s: PixelSurface,
  r: Rect,
  label: string,
  value: string,
  color: Color = UI.ink,
): void {
  const f: BitmapFont = font('body');
  f.draw(s, label, r.x, r.y, UI.inkDim, { caps: true });
  const vw = f.measure(value, { caps: true });
  f.draw(s, value, r.x + r.w - vw, r.y, color, { caps: true });
}

/** Segmented bar — power, capacitor, HP. Segments, never a smooth gradient. */
export function drawBar(s: PixelSurface, r: Rect, frac: number, color: Color): void {
  s.fillRect(r.x, r.y, r.w, r.h, UI.panelRimDark);
  const segs = Math.max(1, Math.floor(r.w / 4));
  const on = Math.round(Math.max(0, Math.min(1, frac)) * segs);
  for (let i = 0; i < segs; i++) {
    s.fillRect(r.x + i * 4 + 1, r.y + 1, 2, r.h - 2, i < on ? color : UI.disabled);
  }
}
